import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { brandSelectTheme } from './reactSelectTheme';
import {
  filterAccountOption,
  formatAccountOption,
  groupPickerOptions,
  noAccountOptionsMessage,
} from '../../../utils/accountPickerOptions';

/**
 * The one account picker (UX_PLAN.md P1, P4).
 *
 * Three surfaces used to hand react-select the picker's *default view* as
 * `options` and rely on its built-in filter, so typing a number outside the
 * view produced the English "No options" — the chart was never searched. Here
 * the menu shows the default view while the input is empty (grouped with the
 * company's most recently used accounts first) and searches the whole chart
 * the moment the user types, with the shared matcher: number by prefix, chart
 * name, imported label.
 *
 * Props beyond react-select's own:
 *   options      the default view (already through buildAccountPickerOptions)
 *   allOptions   the whole chart the search reaches (defaults to `options`)
 *   valueDisplay 'label' | 'number' — how the chosen value renders
 *   groupRecent  put "Senast använda" first when the company has postings
 */
const portalTarget = typeof document !== 'undefined' ? document.body : null;

export default function AccountSelect({
  options,
  allOptions = null,
  value,
  onChange,
  valueDisplay = 'label',
  groupRecent = true,
  placeholder = 'Sök konto (nummer eller namn)…',
  styles = null,
  formatOptionLabel = null,
  ...rest
}) {
  const [inputValue, setInputValue] = useState('');
  const searching = inputValue.trim().length > 0;
  const full = allOptions || options || [];
  const menuOptions = useMemo(() => {
    if (searching) return full;
    return groupRecent ? groupPickerOptions(options || []) : (options || []);
  }, [searching, full, options, groupRecent]);

  const mergedStyles = useMemo(() => ({
    ...(styles || {}),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
      ...(styles && styles.menuPortal ? styles.menuPortal(base) : {}),
    }),
  }), [styles]);

  return (
    <Select
      options={menuOptions}
      value={value}
      onChange={onChange}
      inputValue={inputValue}
      // react-select passes the value the input should take: the typed text
      // on input-change, '' on select/blur/menu-close.
      onInputChange={(next) => setInputValue(next)}
      filterOption={filterAccountOption}
      noOptionsMessage={(ctx) => noAccountOptionsMessage(ctx, { totalCount: full.length })}
      formatOptionLabel={formatOptionLabel || ((option, meta) => formatAccountOption(option, meta, { valueDisplay }))}
      theme={brandSelectTheme}
      menuPortalTarget={portalTarget}
      styles={mergedStyles}
      placeholder={placeholder}
      {...rest}
    />
  );
}
